/**
 * POYO.ai payload shapes — Kling O3 & Kling 3.0 (see project docs / user payload spec).
 */
import { KLING_CLIP_MAX_SEC, MAX_DURATION_SEC, MIN_DURATION_SEC, getClipLimitForModel, snapClipDuration } from "./durationOptions";
import {
  formHasUploadedAudio,
  formHasUploadedImage,
  formHasUploadedVideo,
  formOrScenesHaveImage,
  formOrScenesHaveVideo,
  getGenerationMode,
  getGenerationModeRules,
  sceneHasUploadedImage,
  sceneHasUploadedVideo,
} from "./studioGenerationMode";
import { formHasScenePlan } from "./scenePlan";
import { withExpandedMentionPrompts } from "./mentionAssets";

function formNeedsAutoStitch(form) {
  const limit = getClipLimitForModel(form?.model, {
    hailuoResolution: form?.hailuoResolution,
    resolution: form?.resolution,
  });
  if (!limit.stitchable || limit.maxSec == null) return false;
  return Number(form?.duration) > limit.maxSec;
}

/** UI model id → API model string */
export const POYO_API_MODEL_IDS = {
  "omni-flash": "omni-flash",
  "seedance-2": "seedance-2",
  "seedance-2-fast": "seedance-2-fast",
  "kling-3.0": "kling-3.0/pro",
  "kling-o3-standard": "kling-o3/standard",
  "kling-o3-pro": "kling-o3/pro",
  "kling-o3-4k": "kling-o3/4K",
  "kling-avatar-2.0": "kling-avatar-2.0/standard",
  "kling-3.0-motion-control": "kling-3.0-motion-control",
  "veo-3.1": "veo3.1-fast",
  "hailuo-2.3": "hailuo-2.3",
  "sora-2": "sora-2",
  "sora-2-pro": "sora-2-pro",
  "runway-gen-4.5": "runway-gen-4.5",
  "wan-2.7": "wan2.7-text-to-video",
  "grok-imagine": "grok-imagine",
  "grok-imagine-video-1.5": "grok-imagine-video-1.5",
};

export function resolvePoyoApiModelId(uiModelId, form) {
  const id = String(uiModelId || "");
  if (id === "kling-3.0") {
    return form?.kling30Tier === "pro" ? "kling-3.0/pro" : "kling-3.0/standard";
  }
  if (id === "wan-2.7") {
    return form?.wanMode === "reference" ? "wan2.7-reference-to-video" : "wan2.7-text-to-video";
  }
  if (id === "kling-avatar-2.0") {
    return form?.avatarTier === "pro" ? "kling-avatar-2.0/pro" : "kling-avatar-2.0/standard";
  }
  if (id === "veo-3.1") {
    const variant = form?.veoVariant || "fast";
    return `veo3.1-${variant}`;
  }
  return POYO_API_MODEL_IDS[id] || id;
}

/** @typedef {'kling-o3'|'kling-3'|'kling-motion-control'|'kling-avatar'|'veo'|'omni'|'seedance'|'grok'|'hailuo'|'sora'|'runway'|'wan'|'generic'} ModelFamily */

/**
 * @returns {ModelFamily}
 */
/** Models that output video only — no native audio track (per PoYo API). */
export function modelSupportsNativeAudio(modelId) {
  const id = String(modelId || "").toLowerCase();
  if (id.includes("hailuo") || id.includes("runway")) return false;
  return true;
}

/** Short note when the selected model cannot generate sound. */
export function modelNativeAudioNote(modelId, lang) {
  if (!modelSupportsNativeAudio(modelId)) {
    if (String(modelId || "").includes("hailuo")) {
      return (
        lang?.videoStudioHailuoNoAudioNote ||
        "Hailuo 2.3 outputs silent video only (no audio track). For voiceover, use Kling Avatar 2.0 or Veo 3.1, or add audio in an editor."
      );
    }
    return (
      lang?.videoStudioModelNoAudioNote ||
      "This model outputs silent video only. Pick a model with native audio or add sound in post."
    );
  }
  return "";
}

export function getModelFamily(modelId) {
  const id = String(modelId || "").toLowerCase();
  if (id.includes("motion-control")) return "kling-motion-control";
  if (id.includes("kling-o3")) return "kling-o3";
  if (id.includes("kling-avatar")) return "kling-avatar";
  if (id.includes("kling")) return "kling-3";
  if (id.includes("wan")) return "wan";
  if (id.includes("sora")) return "sora";
  if (id.includes("runway")) return "runway";
  if (id.includes("veo")) return "veo";
  if (id.includes("omni")) return "omni";
  if (id.includes("seedance")) return "seedance";
  if (id.includes("grok")) return "grok";
  if (id.includes("hailuo")) return "hailuo";
  return "generic";
}

function isGrokImagineVideo15(modelId) {
  return String(modelId || "").toLowerCase().includes("grok-imagine-video");
}

/** @param {Array<{ previewUrl?: string }>} files */
export function uploadsToUrls(files) {
  return (files || []).map((f) => f.previewUrl).filter(Boolean);
}

/**
 * Collect voice clips attached under character/source images.
 * @param {Array<{ voiceClip?: object }>} imageUploads
 */
export function voiceClipsFromImages(imageUploads) {
  return (imageUploads || [])
    .map((u) => u?.voiceClip)
    .filter((v) => v && (v.file || v.previewUrl || v.voiceId));
}

/** Filename stem used as the default character / @tag name. */
export function characterLabelFromUpload(upload) {
  const custom = String(upload?.characterName || "").trim();
  if (custom) return custom.slice(0, 28);
  const name = String(upload?.name || upload?.file?.name || "").trim();
  if (name && !/^upload-/i.test(name) && name.length < 40) {
    const stem = name.replace(/\.[a-z0-9]+$/i, "");
    if (stem && stem.length <= 28) return stem;
  }
  return "";
}

/**
 * Per-image character → voice map for multi-speaker dialogue TTS.
 * @param {object} form
 * @returns {Array<{ label: string, index: number, bucket: string, voice_id: string|null, voice_name: string|null }>}
 */
export function collectCharacterVoiceMap(form) {
  /** @type {Array<{ label: string, index: number, bucket: string, voice_id: string|null, voice_name: string|null }>} */
  const out = [];
  const push = (uploads, bucket) => {
    (uploads || []).forEach((u, index) => {
      if (!u?.voiceClip) return;
      const label = characterLabelFromUpload(u) || `Character ${index + 1}`;
      out.push({
        label,
        index,
        bucket,
        voice_id: String(u.voiceClip?.voiceId || "").trim() || null,
        voice_name: String(u.voiceClip?.name || "").trim() || null,
      });
    });
  };
  push(form?.imageUrlUploads, "imageUrlUploads");
  push(form?.referenceImageUploads, "referenceImageUploads");
  (form?.scenes || []).forEach((s, sceneIndex) => {
    (s?.imageUrlUploads || []).forEach((u, index) => {
      if (!u?.voiceClip) return;
      const label =
        characterLabelFromUpload(u) || `Scene ${sceneIndex + 1} image ${index + 1}`;
      out.push({
        label,
        index,
        bucket: "sceneImage",
        voice_id: String(u?.voiceClip?.voiceId || "").trim() || null,
        voice_name: String(u?.voiceClip?.name || "").trim() || null,
      });
    });
  });
  return out;
}

/**
 * Flatten per-image voice clips into referenceAudioUploads for API submit.
 * Includes root images, reference images, and per-scene character images.
 * Library picks set `characterVoiceId` (ElevenLabs voice_id) without a seed file.
 * @param {object} form
 */
export function formWithCharacterVoices(form) {
  // Stitched segment forms already scoped imageUrlUploads to this speaker —
  // do not re-collect every scene's voices (that would collapse to the first voice).
  const isSegment = form?._segmentDuration != null;
  const sceneClips = isSegment
    ? []
    : (form?.scenes || []).flatMap((s) => voiceClipsFromImages(s?.imageUrlUploads));
  const clips = isSegment
    ? voiceClipsFromImages(form?.imageUrlUploads)
    : [
        ...voiceClipsFromImages(form?.imageUrlUploads),
        ...voiceClipsFromImages(form?.referenceImageUploads),
        ...sceneClips,
      ];
  const characterVoices = isSegment
    ? collectCharacterVoiceMap({ imageUrlUploads: form?.imageUrlUploads })
    : collectCharacterVoiceMap(form);
  const explicitId = String(form?.characterVoiceId || "").trim();
  const libraryId =
    (isSegment && explicitId) ||
    characterVoices.map((c) => String(c.voice_id || "").trim()).find(Boolean) ||
    clips.map((c) => String(c?.voiceId || "").trim()).find(Boolean) ||
    explicitId ||
    "";
  if (!clips.length && !(form?.referenceAudioUploads || []).length && !libraryId) {
    return form;
  }
  const existing = form.referenceAudioUploads || [];
  const merged = [];
  const seen = new Set();
  for (const c of [...clips, ...existing]) {
    if (!c) continue;
    // Seed audio for Instant Clone — skip library-only picks (voiceId, no file).
    if (!(c.file || (c.previewUrl && c.source !== "library"))) continue;
    const key = c.id || c.previewUrl || c.file?.name;
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    merged.push(c);
  }
  if (!merged.length && !libraryId) return form;
  return {
    ...form,
    voiceSource: form.voiceSource || (merged.length || libraryId ? "upload" : form.voiceSource),
    sound: form.sound === false ? false : true,
    referenceAudioUploads: merged.slice(0, 5),
    characterVoiceId: libraryId || null,
    characterVoices,
  };
}

/**
 * @typedef {'text-to-video'|'image-to-video'|'reference-to-video'|'multi-shot'} KlingO3Mode
 */

/** @param {object} form */
export function detectKlingO3Mode(form) {
  if (form.multiShots) return "multi-shot";
  if (uploadsToUrls(form.referenceVideoUploads).length > 0) return "video-to-video";
  if (uploadsToUrls(form.referenceImageUploads).length > 0) return "reference-to-video";
  if (uploadsToUrls(form.imageUrlUploads).length > 0) return "image-to-video";
  return "text-to-video";
}

const KLING_O3_MODE_LABELS = {
  "text-to-video": "Text to Video",
  "image-to-video": "Image to Video",
  "video-to-video": "Video to Video",
  "music-to-video": "Music to Video",
  "motion-sync": "Motion Sync",
  "reference-to-video": "Reference to Video",
  "multi-shot": "Multi-Shot",
};

export function klingO3ModeLabel(mode) {
  return KLING_O3_MODE_LABELS[mode] || mode;
}

/** Whether the model supports Kling native voiceover (sound + optional voice clip). */
export function modelSupportsKlingVoiceover(modelId) {
  const family = getModelFamily(modelId);
  return family === "kling-3" || family === "kling-o3";
}

function applyKlingVoiceFields(input, form) {
  const voiceSource = form.voiceSource || "ai";
  const out = { ...input, sound: form.sound !== false };

  if (voiceSource === "preset") {
    const preset = String(form.klingVoicePreset || "").trim();
    if (preset) {
      out.voice_id = preset;
    }
  }

  const voiceClips = uploadsToUrls(form.referenceAudioUploads).slice(0, 1);
  if (voiceSource === "upload" && voiceClips.length) {
    out.reference_audio_urls = voiceClips;
    if (voiceClips[0]) out.audio_url = voiceClips[0];
  }

  return out;
}

function attachKlingAudioFields(input, form, categoryId) {
  const mode = getGenerationMode(categoryId);
  const audioUrls = uploadsToUrls(form.referenceAudioUploads).slice(0, 1);
  if (mode === "music-to-video" && audioUrls.length) {
    return {
      ...input,
      reference_audio_urls: audioUrls,
      audio_url: audioUrls[0],
      sound: true,
    };
  }
  return applyKlingVoiceFields(input, form);
}

/**
 * @param {object} form
 * @param {string} [categoryId]
 * @param {Record<string, string>} [lang]
 * @returns {{ valid: boolean; message?: string }}
 */
/**
 * @param {object} form
 * @param {string} categoryId
 * @param {object} [lang]
 * @param {{ sceneIndex?: number }} [opts] — when set, validate only that scene for per-scene generate
 */
export function validateFormForModel(form, categoryId, lang, opts = {}) {
  const sceneIndex =
    opts.sceneIndex == null || !Number.isFinite(Number(opts.sceneIndex))
      ? null
      : Math.max(0, Math.round(Number(opts.sceneIndex)));

  if (!String(form?.title || "").trim()) {
    return {
      valid: false,
      message: lang?.videoStudioJobTitleRequired || "Title is required.",
    };
  }

  const family = getModelFamily(form.model);
  const mode = getGenerationMode(categoryId);
  const rules = getGenerationModeRules(mode);
  const scenes = Array.isArray(form.scenes) ? form.scenes : [];
  const scene = sceneIndex != null ? scenes[sceneIndex] : null;

  if (rules.requireImages) {
    const ok =
      sceneIndex != null
        ? formHasUploadedImage(form) || sceneHasUploadedImage(scene)
        : formHasScenePlan(form)
          ? formOrScenesHaveImage(form)
          : formHasUploadedImage(form);
    if (!ok) {
      return {
        valid: false,
        message:
          sceneIndex != null
            ? lang?.videoStudioSceneImageRequired ||
              `Upload an image for scene ${sceneIndex + 1}, or add one at the top.`
            : lang?.videoStudioImageRequired || "Upload at least one image to animate.",
      };
    }
  }

  if (rules.requireVideos) {
    const ok =
      sceneIndex != null
        ? formHasUploadedVideo(form) || sceneHasUploadedVideo(scene)
        : formHasScenePlan(form)
          ? formOrScenesHaveVideo(form)
          : formHasUploadedVideo(form);
    if (!ok) {
      return {
        valid: false,
        message:
          sceneIndex != null
            ? lang?.videoStudioSceneVideoRequired ||
              `Upload a reference video for scene ${sceneIndex + 1}.`
            : lang?.videoStudioVideoRequired ||
              "Upload a reference video (MP4, WebM, or MOV).",
      };
    }
  }

  if (rules.requireMusic && !formHasUploadedAudio(form)) {
    return {
      valid: false,
      message:
        lang?.videoStudioMusicRequired || "Upload a music track (MP3, WAV, or M4A).",
    };
  }

  const duration = Number(form.duration) || 0;
  if (
    family !== "kling-motion-control" &&
    (duration < MIN_DURATION_SEC || duration > MAX_DURATION_SEC)
  ) {
    // Per-scene generate may temporarily total under the global min while clips stay valid.
    if (!(sceneIndex != null && formHasScenePlan(form) && duration >= 1)) {
      return {
        valid: false,
        message:
          lang?.videoStudioDurationRange?.replace("{min}", String(MIN_DURATION_SEC)).replace(
            "{max}",
            String(MAX_DURATION_SEC),
          ) || `Duration must be between ${MIN_DURATION_SEC} and ${MAX_DURATION_SEC} seconds.`,
      };
    }
  }

  if (formHasScenePlan(form) || formNeedsAutoStitch(form)) {
    if (!scenes.length) {
      return {
        valid: false,
        message:
          lang?.videoStudioScenesRequired ||
          "This duration needs multiple scenes — wait for the scene list to load, or adjust duration.",
      };
    }
    if (sceneIndex != null) {
      if (!scenes[sceneIndex]) {
        return {
          valid: false,
          message:
            lang?.videoStudioSceneMissing ||
            `Scene ${sceneIndex + 1} is missing from the plan.`,
        };
      }
      if (!String(scenes[sceneIndex].prompt || "").trim()) {
        return {
          valid: false,
          message: (lang?.videoStudioScenePromptRequired || "Add a prompt for scene {n}.")
            .replace("{n}", String(sceneIndex + 1)),
        };
      }
    } else {
      const emptyIdx = scenes.findIndex((s) => !String(s.prompt || "").trim());
      if (emptyIdx >= 0) {
        return {
          valid: false,
          message: (lang?.videoStudioScenePromptRequired || "Add a prompt for scene {n}.")
            .replace("{n}", String(emptyIdx + 1)),
        };
      }
    }
  }

  if (
    modelSupportsKlingVoiceover(form.model) &&
    rules.showVoiceover &&
    form.voiceSource === "upload" &&
    !formHasUploadedAudio(form)
  ) {
    return {
      valid: false,
      message:
        lang?.videoStudioVoiceUploadRequired || "Upload a voice clip or switch to AI / preset voice.",
    };
  }

  if (
    modelSupportsKlingVoiceover(form.model) &&
    rules.showVoiceover &&
    form.voiceSource === "preset" &&
    !String(form.klingVoicePreset || "").trim()
  ) {
    return {
      valid: false,
      message:
        lang?.videoStudioVoicePresetRequired || "Choose a Kie AI voice or switch to AI / upload voice.",
    };
  }

  if (mode === "text-to-video" && formHasUploadedImage(form)) {
    return {
      valid: false,
      message:
        lang?.videoStudioTextModeNoImages ||
          "Text to Video does not use image uploads — switch to Image to Video or remove images.",
    };
  }

  if (mode === "text-to-video" && formHasUploadedVideo(form)) {
    return {
      valid: false,
      message:
        lang?.videoStudioTextModeNoVideos ||
          "Text to Video does not use video uploads — switch to Video to Video or remove the clip.",
    };
  }

  if (
    mode === "text-to-video" &&
    formHasUploadedAudio(form) &&
    form.voiceSource !== "upload"
  ) {
    return {
      valid: false,
      message:
        lang?.videoStudioTextModeNoAudio ||
          "Remove the audio upload or choose Upload voice under Voiceover.",
    };
  }

  if ((family === "kling-o3" || family === "kling-3") && form.multiShots) {
    const shots = (form.multiPrompt || []).filter((s) => String(s.prompt || "").trim());
    if (!shots.length) {
      return { valid: false, message: "Add at least one shot prompt when Multi-shot is enabled." };
    }
    const total = (form.multiPrompt || []).reduce((s, x) => s + (Number(x.duration) || 0), 0);
    if (total > 15) {
      return {
        valid: false,
        message: `${family === "kling-3" ? "Kling 3.0" : "Kling O3"} multi-shot: total shot duration cannot exceed 15s.`,
      };
    }
    for (const s of form.multiPrompt || []) {
      const d = Number(s.duration) || 0;
      const maxShot = family === "kling-3" ? 12 : 5;
      if (d < 1 || d > maxShot) {
        return {
          valid: false,
          message: `Each shot must be 1–${maxShot} seconds.`,
        };
      }
    }
  }

  if (family === "kling-motion-control") {
    const hasImage = (form.imageUrlUploads || []).some((f) => f.file || f.previewUrl);
    const hasVideo = (form.referenceVideoUploads || []).some((f) => f.file || f.previewUrl);
    if (!hasImage) {
      return { valid: false, message: "Character reference image is required." };
    }
    if (!hasVideo) {
      return { valid: false, message: "Motion reference video is required (MP4/MOV)." };
    }
  }

  if (family === "wan") {
    const d = Number(form.duration) || 5;
    const stitching = formNeedsAutoStitch(form);
    if (!stitching && (d < 2 || d > 10)) {
      return { valid: false, message: "Wan 2.7: duration must be between 2 and 10 seconds." };
    }
    if (form.wanMode === "reference") {
      const hasRef =
        (form.referenceImageUploads || []).some((f) => f.file || f.previewUrl) ||
        (form.referenceVideoUploads || []).some((f) => f.file);
      if (!hasRef) {
        return {
          valid: false,
          message: "Add at least one reference image or video for Wan reference mode.",
        };
      }
    }
  }

  if (family === "kling-avatar") {
    const hasImage = (form.imageUrlUploads || []).some((f) => f.file || f.previewUrl);
    const hasAudio = (form.referenceAudioUploads || []).some((f) => f.file);
    if (!hasImage) {
      return { valid: false, message: "Avatar photo is required." };
    }
    if (!hasAudio) {
      return {
        valid: false,
        message: "Driving audio is required (2–60 seconds, MP3/WAV).",
      };
    }
    return { valid: true };
  }

  if (family === "omni") {
    const imgs = (form.imageUrlUploads || []).filter((f) => f.file || f.previewUrl);
    if (imgs.length === 2) {
      return {
        valid: false,
        message:
          "Omni Flash accepts 0, 1, or 3 images (not 2). Use one for image-to-video or three for reference fusion.",
      };
    }
    const d = Number(form.duration) || 6;
    const stitching = formNeedsAutoStitch(form);
    const vids = (form.referenceVideoUploads || []).filter((f) => f.file || f.previewUrl);
    if (!stitching && !vids.length && ![4, 6, 8, 10].includes(d)) {
      return { valid: false, message: "Omni Flash: choose 4s, 6s, 8s, or 10s duration." };
    }
  }

  if (family === "seedance") {
    const d = Number(form.duration) || 8;
    const stitching = formNeedsAutoStitch(form);
    if (!stitching && (d < 4 || d > 15)) {
      return { valid: false, message: "Seedance 2: duration must be between 4s and 15s." };
    }
    if (String(form.model || "").includes("fast")) {
      const res = String(form.resolution || "720p").toLowerCase();
      if (res === "1080p" || res === "4k") {
        return {
          valid: false,
          message:
            "Seedance 2 Fast supports 480p and 720p only. Switch to Seedance 2 for 1080p/4K.",
        };
      }
    }
  }

  if (family === "grok") {
    const topImgs = (form.imageUrlUploads || []).filter((f) => f.file || f.previewUrl);
    const sceneImgs =
      sceneIndex != null
        ? (form.scenes?.[sceneIndex]?.imageUrlUploads || []).filter((f) => f.file || f.previewUrl)
        : [];
    const imgs = sceneImgs.length ? sceneImgs : topImgs;
    const stitching = formNeedsAutoStitch(form) || formHasScenePlan(form);
    const d =
      sceneIndex != null
        ? Number(form.scenes?.[sceneIndex]?.duration) || Number(form.duration) || 6
        : Number(form.duration) || 6;
    if (isGrokImagineVideo15(form.model)) {
      // Scene cards may hold the start frame(s); PoYo accepts one image — we send the first.
      if (sceneIndex != null) {
        if (imgs.length < 1) {
          return {
            valid: false,
            message:
              lang?.videoStudioGrokSceneImageRequired ||
              `Grok Imagine Video 1.5 needs one source image on scene ${sceneIndex + 1}.`,
          };
        }
      } else if (formHasScenePlan(form)) {
        for (let si = 0; si < scenes.length; si += 1) {
          if (!sceneHasUploadedImage(scenes[si]) && topImgs.length < 1) {
            return {
              valid: false,
              message: (
                lang?.videoStudioGrokSceneImageRequired ||
                "Grok Imagine Video 1.5 needs a source image on scene {n}."
              ).replace("{n}", String(si + 1)),
            };
          }
        }
      } else if (imgs.length < 1) {
        return {
          valid: false,
          message: "Grok Imagine Video 1.5 requires at least one source image.",
        };
      }
      if (!stitching && (d < 1 || d > 15)) {
        return {
          valid: false,
          message: "Grok Imagine Video 1.5: duration must be between 1s and 15s.",
        };
      }
      const res = String(form.resolution || "720p").toLowerCase();
      if (res !== "480p" && res !== "720p") {
        return {
          valid: false,
          message: "Grok Imagine Video 1.5 supports 480p and 720p only.",
        };
      }
    } else {
      if (imgs.length > 1) {
        return {
          valid: false,
          message: "Grok Imagine accepts at most one image (optional for text-to-video).",
        };
      }
      if (!stitching && d !== 6 && d !== 10) {
        return { valid: false, message: "Grok Imagine: choose 6s or 10s duration." };
      }
    }
  }

  if (family === "hailuo") {
    const res = form.hailuoResolution || form.resolution || "768p";
    const duration = Number(form.duration) || 6;
    const stitching = formNeedsAutoStitch(form);
    if (res === "1080p" && !stitching && duration !== 6) {
      return {
        valid: false,
        message: "Hailuo 2.3: 1080p only supports 6 second duration.",
      };
    }
  }

  if (family === "sora") {
    const d = Number(form.duration) || 15;
    const isPro = String(form.model || "").includes("pro");
    const stitching = formNeedsAutoStitch(form);
    if (!stitching) {
      if (isPro && d !== 15 && d !== 25) {
        return { valid: false, message: "Sora 2 Pro: choose 15s or 25s duration." };
      }
      if (!isPro && d !== 10 && d !== 15) {
        return { valid: false, message: "Sora 2: choose 10s or 15s duration." };
      }
    }
  }

  if (family === "runway") {
    const d = Number(form.duration) || 5;
    const stitching = formNeedsAutoStitch(form);
    if (!stitching && d !== 5 && d !== 10) {
      return { valid: false, message: "Runway Gen-4.5: choose 5s or 10s duration." };
    }
  }

  const promptOptional =
    family === "kling-motion-control" ||
    mode === "music-to-video" ||
    mode === "motion-sync" ||
    ((family === "kling-o3" || family === "kling-3") && form.multiShots) ||
    formHasScenePlan(form);
  if (!promptOptional && !String(form.prompt || "").trim()) {
    return { valid: false, message: "Prompt is required." };
  }

  return { valid: true };
}

/**
 * @param {object} form
 * @param {string} [categoryId]
 */
export function buildPoyoPayload(form, categoryId) {
  // Expand @-mention tokens to natural language; media still travels via URL arrays.
  form = withExpandedMentionPrompts(form);
  const family = getModelFamily(form.model);
  const apiModel = resolvePoyoApiModelId(form.model, form);
  const callback_url = `${window.location.origin}/api/video-studio/callback`;

  if (family === "kling-avatar") {
    const input = buildKlingAvatarInput(form);
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input,
      _meta: { family },
    };
  }

  if (family === "veo") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildVeo31Input(form),
      _meta: { family, variant: form.veoVariant || "fast" },
    };
  }

  if (family === "omni") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildOmniFlashInput(form),
      _meta: { family },
    };
  }

  if (family === "seedance") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildSeedance2Input(form),
      _meta: { family },
    };
  }

  if (family === "grok") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: isGrokImagineVideo15(form.model)
        ? buildGrokImagineVideo15Input(form)
        : buildGrokImagineInput(form),
      _meta: { family, variant: isGrokImagineVideo15(form.model) ? "1.5" : "imagine" },
    };
  }

  if (family === "hailuo") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildHailuo23Input(form),
      _meta: { family },
    };
  }

  if (family === "sora") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildSora2ProInput(form),
      _meta: { family },
    };
  }

  if (family === "runway") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildRunwayGen45Input(form),
      _meta: { family },
    };
  }

  if (family === "kling-motion-control") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildKlingMotionControlInput(form),
      _meta: { family },
    };
  }

  if (family === "kling-3") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildKling30Input(form, categoryId),
      _meta: { family, multiShots: Boolean(form.multiShots) },
    };
  }

  if (family === "wan") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildWan27Input(form),
      _meta: { family, wanMode: form.wanMode || "text" },
    };
  }

  if (family === "kling-o3") {
    return {
      model: apiModel,
      callback_url,
      categoryId,
      input: buildKlingO3Input(form, categoryId),
      _meta: { family, mode: detectKlingO3Mode(form) },
    };
  }

  return {
    model: apiModel,
    callback_url,
    categoryId,
    input: buildGenericInput(form, categoryId),
    _meta: { family },
  };
}

function buildKling30Input(form, categoryId) {
  const multiShots = Boolean(form.multiShots);
  const sound = multiShots ? true : form.sound !== false;

  if (multiShots) {
    const multi_prompt = (form.multiPrompt || [])
      .filter((s) => String(s.prompt || "").trim())
      .map((s) => ({
        prompt: String(s.prompt).trim(),
        duration: Math.min(12, Math.max(1, Number(s.duration) || 3)),
      }));
    const totalDuration = multi_prompt.reduce((sum, s) => sum + s.duration, 0);
    const input = {
      multi_shots: true,
      sound,
      multi_prompt,
      duration: totalDuration || Number(form.duration) || 9,
      aspect_ratio: form.aspectRatio || "16:9",
    };
    const startFrame = uploadsToUrls(form.imageUrlUploads).slice(0, 1);
    if (startFrame.length) input.image_urls = startFrame;
    return input;
  }

  const segDur = Number(form._segmentDuration ?? form.duration) || 5;
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: Math.min(KLING_CLIP_MAX_SEC, Math.max(MIN_DURATION_SEC, segDur)),
    multi_shots: false,
    sound,
    aspect_ratio: form.aspectRatio || "16:9",
  };
  const image_urls = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
  const reference_image_urls = uploadsToUrls(form.referenceImageUploads).slice(0, 4);
  const reference_video_urls = uploadsToUrls(form.referenceVideoUploads).slice(0, 3);
  if (image_urls.length) input.image_urls = image_urls;
  if (reference_image_urls.length) input.reference_image_urls = reference_image_urls;
  if (reference_video_urls.length) input.reference_video_urls = reference_video_urls;
  return attachKlingAudioFields(input, form, categoryId);
}

function buildKlingMotionControlInput(form) {
  const input = {
    character_orientation: form.characterOrientation || "image",
    resolution: form.motionResolution || "720p",
  };
  const prompt = String(form.prompt || "").trim();
  if (prompt) input.prompt = prompt;
  return input;
}

function buildWan27Input(form) {
  const limit = getClipLimitForModel(form.model || "wan-2.7");
  const duration = snapClipDuration(
    Number(form._segmentDuration ?? form.duration) || 5,
    limit,
  );
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration,
    aspect_ratio: form.aspectRatio || "16:9",
    resolution: form.resolution || "720p",
  };
  if (form.seed !== "" && form.seed != null && !Number.isNaN(Number(form.seed))) {
    input.seed = Number(form.seed);
  }
  if (form.wanMode === "reference") {
    const refImages = uploadsToUrls(form.referenceImageUploads).slice(0, 9);
    const refVideos = uploadsToUrls(form.referenceVideoUploads).slice(0, 3);
    if (refImages.length) input.reference_image_urls = refImages;
    if (refVideos.length) input.reference_video_urls = refVideos;
  } else {
    const frames = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
    if (frames.length) input.image_urls = frames;
  }
  return input;
}

function buildKlingO3Input(form, categoryId) {
  const mode = detectKlingO3Mode(form);
  const rawDur = Number(form._segmentDuration ?? form.duration) || 5;
  const duration = Math.min(KLING_CLIP_MAX_SEC, Math.max(MIN_DURATION_SEC, rawDur));
  const sound = form.sound !== false;

  if (mode === "multi-shot") {
    const multi_prompt = (form.multiPrompt || [])
      .filter((s) => String(s.prompt || "").trim())
      .map((s) => ({
        prompt: String(s.prompt).trim(),
        duration: Number(s.duration) || 5,
      }));
    const totalDuration = multi_prompt.reduce((sum, s) => sum + s.duration, 0);
    return {
      duration: totalDuration || duration,
      multi_shots: true,
      sound,
      multi_prompt,
    };
  }

  /** @type {Record<string, unknown>} */
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration,
    multi_shots: false,
    sound,
    aspect_ratio: form.aspectRatio || "16:9",
  };

  const image_urls = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
  const reference_image_urls = uploadsToUrls(form.referenceImageUploads).slice(0, 4);
  const reference_video_urls = uploadsToUrls(form.referenceVideoUploads).slice(0, 3);

  if (image_urls.length) input.image_urls = image_urls;
  if (reference_image_urls.length) input.reference_image_urls = reference_image_urls;
  if (reference_video_urls.length) input.reference_video_urls = reference_video_urls;

  return attachKlingAudioFields(input, form, categoryId);
}

/** Re-assign uploads when switching models. */
export function migrateMediaForModel(form, newModelId) {
  const family = getModelFamily(newModelId);
  let imageUrlUploads = [...(form.imageUrlUploads || [])];
  let referenceImageUploads = [...(form.referenceImageUploads || [])];
  let referenceVideoUploads = [...(form.referenceVideoUploads || [])];
  let referenceAudioUploads = [...(form.referenceAudioUploads || [])];

  if (form.uploads?.length) {
    const legacy = form.uploads;
    if (family === "kling-avatar") {
      imageUrlUploads = legacy.slice(0, 1);
      referenceAudioUploads = legacy.slice(1, 2);
    } else if (family === "kling-o3") {
      imageUrlUploads = legacy.slice(0, 2);
      referenceImageUploads = legacy.slice(2, 6);
    } else if (family === "veo" || family === "hailuo") {
      imageUrlUploads = legacy.slice(0, 2);
    } else if (family === "sora" || family === "runway") {
      imageUrlUploads = legacy.slice(0, 1);
    } else {
      imageUrlUploads = legacy.slice(0, 2);
    }
    return {
      imageUrlUploads,
      referenceImageUploads,
      referenceVideoUploads,
      referenceAudioUploads,
      uploads: [],
    };
  }

  if (family === "kling-avatar") {
    return {
      imageUrlUploads,
      referenceImageUploads: [],
      referenceVideoUploads: [],
      referenceAudioUploads,
    };
  }

  if (family === "kling-motion-control") {
    return {
      imageUrlUploads: imageUrlUploads.slice(0, 1),
      referenceImageUploads: [],
      referenceVideoUploads,
      referenceAudioUploads: [],
    };
  }

  if (family === "wan") {
    return {
      imageUrlUploads,
      referenceImageUploads,
      referenceVideoUploads,
      referenceAudioUploads: [],
    };
  }

  if (family === "grok") {
    return {
      imageUrlUploads: imageUrlUploads.slice(0, 1),
      referenceImageUploads: [],
      referenceVideoUploads: [],
      // Keep mic voice clips attached under the character image.
      referenceAudioUploads,
    };
  }

  return { imageUrlUploads, referenceImageUploads, referenceVideoUploads, referenceAudioUploads };
}

function buildKlingAvatarInput(form) {
  /** @type {Record<string, unknown>} */
  const input = {};
  const prompt = String(form.prompt || "").trim();
  if (prompt) input.prompt = prompt;
  const image_urls = uploadsToUrls(form.imageUrlUploads).slice(0, 1);
  if (image_urls.length) input.image_urls = image_urls;
  return input;
}

function buildVeo31Input(form) {
  const variant = form.veoVariant || "fast";
  const limit = getClipLimitForModel(form.model || "veo-3.1");
  const duration = snapClipDuration(
    Number(form._segmentDuration ?? form.duration) || limit.maxSec || 8,
    limit,
  );
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration,
    aspect_ratio: form.aspectRatio || "16:9",
    resolution: form.resolution || "720p",
    _veo_variant: variant,
  };
  if (variant !== "lite") {
    const urls = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
    if (urls.length) input.image_urls = urls;
  }
  return input;
}

function buildOmniFlashInput(form) {
  const limit = getClipLimitForModel(form.model || "omni-flash");
  const videoUrls = uploadsToUrls(form.referenceVideoUploads).slice(0, 1);
  const imageUrls = uploadsToUrls(form.imageUrlUploads);
  // PoYo Omni Flash: 0, 1, or 3 images (not 2).
  let images = imageUrls;
  if (images.length === 2) images = images.slice(0, 1);
  if (images.length > 3) images = images.slice(0, 3);

  const resRaw = String(form.resolution || "1080p");
  const resolution =
    resRaw === "4K" || resRaw.toLowerCase() === "4k"
      ? "4k"
      : resRaw === "720p" || resRaw === "1080p"
        ? resRaw
        : "1080p";

  /** @type {Record<string, unknown>} */
  const input = {
    prompt: String(form.prompt || "").trim(),
    resolution,
    aspect_ratio: form.aspectRatio || "16:9",
  };

  if (images.length) input.image_urls = images;
  if (videoUrls.length) {
    input.video_urls = videoUrls;
    // Duration is omitted for video-input jobs on PoYo.
  } else {
    input.duration = snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || 6,
      limit,
    );
  }
  return input;
}

function buildSeedance2Input(form) {
  const limit = getClipLimitForModel(form.model || "seedance-2");
  const isFast = String(form.model || "").includes("fast");
  let resolution = String(form.resolution || "720p");
  if (isFast && (resolution === "1080p" || resolution === "4K" || resolution === "4k")) {
    resolution = "720p";
  }
  if (resolution === "4K") resolution = "4k";

  /** @type {Record<string, unknown>} */
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || 8,
      limit,
    ),
    resolution,
    aspect_ratio: form.aspectRatio || "16:9",
    generate_audio: form.generateAudio !== false,
  };

  const frames = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
  if (frames.length) input.image_urls = frames;

  const refImages = uploadsToUrls(form.referenceImageUploads).slice(0, 9);
  if (refImages.length) input.reference_image_urls = refImages;

  const refVideos = uploadsToUrls(form.referenceVideoUploads).slice(0, 3);
  if (refVideos.length) input.reference_video_urls = refVideos;

  const refAudios = uploadsToUrls(form.referenceAudioUploads).slice(0, 3);
  if (refAudios.length) input.reference_audio_urls = refAudios;

  return input;
}

const GROK_ASPECTS = new Set(["1:1", "2:3", "3:2", "16:9", "9:16"]);
const GROK_MODES = new Set(["fun", "normal", "spicy"]);

function buildGrokImagineInput(form) {
  const limit = getClipLimitForModel(form.model || "grok-imagine");
  const aspect = String(form.aspectRatio || "16:9");
  const modeRaw = String(form.grokMode || "normal").toLowerCase();
  /** @type {Record<string, unknown>} */
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || 6,
      limit,
    ),
    mode: GROK_MODES.has(modeRaw) ? modeRaw : "normal",
  };
  const images = uploadsToUrls(form.imageUrlUploads).slice(0, 1);
  if (images.length) {
    input.image_urls = images;
  } else {
    input.aspect_ratio = GROK_ASPECTS.has(aspect) ? aspect : "16:9";
  }
  return input;
}

function buildGrokImagineVideo15Input(form) {
  const limit = getClipLimitForModel(form.model || "grok-imagine-video-1.5");
  let resolution = String(form.resolution || "720p").toLowerCase();
  if (resolution !== "480p" && resolution !== "720p") resolution = "720p";
  /** @type {Record<string, unknown>} */
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || 6,
      limit,
    ),
    resolution,
  };
  const images = uploadsToUrls(form.imageUrlUploads).slice(0, 1);
  if (images.length) input.image_urls = images;
  return input;
}

function buildSora2ProInput(form) {
  const limit = getClipLimitForModel(form.model || "sora-2-pro");
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || limit.maxSec || 15,
      limit,
    ),
    aspect_ratio: form.aspectRatio || "16:9",
  };
  const style = String(form.soraStyle || "").trim();
  if (style) input.style = style;
  if (form.soraStoryboard) input.storyboard = true;
  return input;
}

function buildRunwayGen45Input(form) {
  const limit = getClipLimitForModel(form.model || "runway-gen-4.5");
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: snapClipDuration(
      Number(form._segmentDuration ?? form.duration) || limit.maxSec || 5,
      limit,
    ),
    aspect_ratio: form.aspectRatio || "16:9",
  };
  if (form.seed !== "" && form.seed != null && !Number.isNaN(Number(form.seed))) {
    input.seed = Number(form.seed);
  }
  return input;
}

function buildHailuo23Input(form) {
  const resolution = form.hailuoResolution || form.resolution || "768p";
  const limit = getClipLimitForModel(form.model || "hailuo-2.3", {
    hailuoResolution: resolution,
    resolution,
  });
  let duration = snapClipDuration(
    Number(form._segmentDuration ?? form.duration) || limit.minSec || 6,
    limit,
  );
  if (resolution === "1080p") duration = 6;
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration,
    resolution,
    prompt_optimizer: form.promptOptimizer !== false,
  };
  return input;
}

function buildGenericInput(form, categoryId) {
  const input = {
    prompt: String(form.prompt || "").trim(),
    duration: Number(form.duration) || 5,
    aspect_ratio: form.aspectRatio || "16:9",
    generate_audio: form.generateAudio !== false,
  };
  const urls = uploadsToUrls(form.imageUrlUploads).slice(0, 2);
  if (urls.length) input.image_urls = urls;
  const refs = uploadsToUrls(form.referenceImageUploads).slice(0, 4);
  if (refs.length) input.reference_image_urls = refs;
  const refVideos = uploadsToUrls(form.referenceVideoUploads).slice(0, 3);
  if (refVideos.length) input.reference_video_urls = refVideos;
  if (modelSupportsKlingVoiceover(form.model) || getGenerationMode(categoryId) === "music-to-video") {
    return attachKlingAudioFields(input, form, categoryId);
  }
  return input;
}
